import { asyncFrame } from '@1inch-community/core/async';
import { appendStyle } from '@1inch-community/core/lit-utils';
import { html, render } from 'lit';
import { distinctUntilChanged, map, shareReplay, startWith, Subject } from 'rxjs';
import { ScrollViewProviderElement } from '../scroll';
import { slideAnimation } from './animations';
import { SceneWrapperElement } from './scene-wrapper.element';
import { sceneStyle } from './scene.style';
export class SceneController {
    static { this.styles = sceneStyle; }
    get activeScene() {
        return this.sceneStack[this.sceneStack.length - 1];
    }
    constructor(rootSceneName, config, animation = slideAnimation()) {
        this.rootSceneName = rootSceneName;
        this.config = config;
        this.animation = animation;
        this.takeUpdate$ = new Subject();
        this.sceneStack = [];
        this.sceneContainer = buildSceneContainer();
        this.transitionInProgress = false;
        this.currentSceneName$ = this.takeUpdate$.pipe(startWith(null), map(() => this.getCurrentSceneName()), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        this.sceneStack.push(rootSceneName);
    }
    render(config) {
        if (this.transitionInProgress) {
            return html `${this.sceneContainer}`;
        }
        const isFirstRender = !this.currentScenes;
        this.currentScenes = config;
        const sceneName = this.getCurrentSceneName();
        const currentSceneConfig = this.config[sceneName];
        const isLazyRenderScene = currentSceneConfig.lazyRender ?? false;
        if (!isLazyRenderScene || isFirstRender) {
            const sceneFactory = this.getScene(sceneName);
            if (!sceneFactory) {
                throw new Error(`Scene not exist`);
            }
            const sceneWrapper = this.buildSceneWrapper(sceneFactory(), sceneName);
            this.clearContainer();
            this.sceneContainerAppendChild(sceneWrapper);
            this.applySceneConfigBySceneName(sceneName);
        }
        this.takeUpdate$.next();
        return html `${this.sceneContainer}`;
    }
    async nextTo(sceneName, immediate = false) {
        if (this.transitionInProgress) {
            return;
        }
        this.sceneStack.push(sceneName);
        await this.transition(sceneName, false, immediate);
        this.takeUpdate$.next();
    }
    async back() {
        if (this.transitionInProgress) {
            return;
        }
        const sceneName = (this.sceneStack[this.sceneStack.length - 2] ?? this.rootSceneName);
        if (this.sceneStack.length > 1) {
            this.sceneStack.pop();
        }
        await this.transition(sceneName, true);
        this.takeUpdate$.next();
    }
    resetScene() {
        this.sceneStack = [this.rootSceneName];
        this.takeUpdate$.next();
    }
    getCurrentSceneName() {
        let currentScene = this.rootSceneName;
        if (this.sceneContainer.firstChild) {
            currentScene = this.sceneContainer.firstChild.id;
        }
        return currentScene;
    }
    async transition(sceneName, isBack = false, immediate = false) {
        this.transitionInProgress = true;
        try {
            const currentScene = this.getCurrentSceneName();
            if (currentScene === sceneName) {
                return;
            }
            const nextSceneFactory = this.getScene(sceneName);
            if (!nextSceneFactory) {
                throw new Error(`Scene ${sceneName} not exist`);
            }
            const nextSceneWrapper = this.buildSceneWrapper(nextSceneFactory(), sceneName);
            const currentSceneWrapper = this.sceneContainer.firstChild;
            appendStyle(currentSceneWrapper, {
                pointerEvents: 'none',
            });
            appendStyle(nextSceneWrapper, {
                pointerEvents: 'none',
            });
            const upScene = isBack ? currentSceneWrapper : nextSceneWrapper;
            const downScene = !isBack ? currentSceneWrapper : nextSceneWrapper;
            isBack ? upScene.animationOutStart() : upScene.animationInStart();
            isBack ? downScene.animationOutStart() : downScene.animationInStart();
            this.sceneContainerAppendChild(nextSceneWrapper);
            await asyncFrame();
            const nextSceneWrapperRect = nextSceneWrapper.getBoundingClientRect();
            const currentSceneWrapperRect = currentSceneWrapper.getBoundingClientRect();
            await this.animation.preparation(upScene, downScene, isBack ?? false);
            if (nextSceneWrapperRect.height > currentSceneWrapperRect.height) {
                this.applySceneConfigBySceneName(sceneName);
            }
            await Promise.all([
                this.animation.transition(upScene, downScene, isBack ?? false, immediate),
                this.resizeContainer(nextSceneWrapperRect, currentSceneWrapperRect, immediate),
            ]);
            if (nextSceneWrapperRect.height < currentSceneWrapperRect.height) {
                this.applySceneConfigBySceneName(sceneName);
            }
            appendStyle(this.sceneContainer, {
                width: '',
                height: '',
            });
            appendStyle(currentSceneWrapper, {
                pointerEvents: '',
            });
            appendStyle(nextSceneWrapper, {
                pointerEvents: '',
            });
            this.sceneContainer.firstChild &&
                this.sceneContainer.removeChild(this.sceneContainer.firstChild);
            isBack ? upScene.animationOutEnd() : upScene.animationInEnd();
            isBack ? downScene.animationOutEnd() : downScene.animationInEnd();
        }
        finally {
            this.transitionInProgress = false;
        }
    }
    getScene(sceneName) {
        if (!this.currentScenes) {
            return null;
        }
        return this.currentScenes[sceneName] ?? null;
    }
    clearContainer() {
        while (this.sceneContainer.firstChild) {
            this.sceneContainer.removeChild(this.sceneContainer.firstChild);
        }
    }
    buildSceneWrapper(content, name) {
        const sceneWrapper = document.createElement(SceneWrapperElement.tagName);
        sceneWrapper.id = name;
        sceneWrapper.classList.add('scene-wrapper', name);
        render(content, sceneWrapper);
        return sceneWrapper;
    }
    sceneContainerAppendChild(sceneWrapper) {
        this.sceneContainer.appendChild(sceneWrapper);
    }
    applySceneConfigBySceneName(sceneName) {
        const config = this.config[sceneName];
        this.applySceneSizes(config);
    }
    applySceneSizes(config) {
        const formatValue = (value) => {
            if (!value) {
                return '';
            }
            return typeof value === 'number' ? `${value}px` : value;
        };
        if (config.maxHeight) {
            this.sceneContainer.maxHeight = parseInt(config.maxHeight.toString());
        }
        appendStyle(this.sceneContainer, {
            minWidth: formatValue(config.minWidth),
            maxWidth: formatValue(config.maxWidth),
        });
    }
    async resizeContainer(nextRect, currentRect, immediate) {
        const fromKeyframe = {
            height: `${currentRect.height}px`,
            width: `${currentRect.width}px`,
        };
        const toKeyframe = {
            height: `${nextRect.height}px`,
            width: `${nextRect.width}px`,
        };
        appendStyle(this.sceneContainer, fromKeyframe);
        await this.sceneContainer.animate([fromKeyframe, toKeyframe], {
            duration: immediate ? 1 : 500,
            easing: 'cubic-bezier(.2, .8, .2, 1)',
        }).finished;
        appendStyle(this.sceneContainer, toKeyframe);
    }
}
function buildSceneContainer() {
    const sceneContainer = document.createElement(ScrollViewProviderElement.tagName);
    sceneContainer.id = 'scene-container';
    sceneContainer.classList.add('scene-container');
    return sceneContainer;
}
//# sourceMappingURL=scene.controller.js.map